/**
 * Notes drawer — freeform per-user notebook for market thoughts,
 * not tied to any the trading server trade. Mirrors TradeJournal's drawer chrome
 * + attachment patterns but doesn't need the
 * Open/Pending/Closed/Hidden section partitioning since notes are
 * just a flat list keyed by UUID.
 *
 * Each note row: title (preview line) + body (long text) + a small
 * SYMBOL · TF context badge captured from the active chart at
 * creation + zero-or-more PNG attachments (auto-screenshot or
 * file upload).
 */
import { useEffect, useRef, useState } from "react";
import {
  useActiveTabId,
  useAppDispatch,
  useAppSelector,
} from "@/state/hooks";
import {
  clearNoteFocus,
  setImageBoardOpen,
  setNotesOpen,
} from "@/state/slices/uiSlice";
import {
  createNote,
  deleteNote,
  deleteNoteAttachment,
  fetchNotes,
  updateNote,
  updateNoteAttachment,
  uploadNoteAttachment,
} from "@/state/slices/notesSlice";
import type { Note, NoteAttachment } from "@/api/types";
import { captureChart } from "@/utils/chartCapture";
import { fmtShortDateTimeIso } from "@/utils/chartTime";
import { tfMt5Label } from "@/utils/timeframe";
import { apiUrl } from "@/api/client";
import { AttachmentThumb } from "@/components/AttachmentThumb/AttachmentThumb";
import {
  LinkedTextArea,
  RefText,
  type LinkedTextAreaHandle,
} from "@/components/RefText/RefText";
import { ImageEditor } from "@/components/ImageEditor/ImageEditor";
import {
  ImageLightbox,
  type GalleryImage,
} from "@/components/ImageLightbox/ImageLightbox";
import styles from "./Notes.module.css";

/** URL the attachment blob streams from. */
function attachmentUrl(noteId: string, attId: string): string {
  return apiUrl(
    `/api/notes/${encodeURIComponent(noteId)}/attachments/${encodeURIComponent(attId)}`,
  );
}

/** Display label for an attachment.
 *
 *  Falls back to the position for anything the server backfill
 *  hasn't reached yet — the backfill runs on the list endpoint, so
 *  in practice only an attachment uploaded by an older build during
 *  this very session can be missing one. */
export function labelFor(att: NoteAttachment, index: number): string {
  return att.label || String(index + 1);
}

export function Notes() {
  const dispatch = useAppDispatch();
  const open = useAppSelector((s) => s.ui.notesOpen);
  const notes = useAppSelector((s) => s.notes.list);
  const status = useAppSelector((s) => s.notes.status);
  const activeTabId = useActiveTabId();
  const activeTabUi = useAppSelector((s) => s.ui.byTab[activeTabId]);
  // One-shot "jump to this note" request parked by the Image Board.
  const focusNoteId = useAppSelector((s) => s.ui.focusNoteId);
  // Row elements by note id, so a jump can scroll the right one in.
  const rowRefs = useRef(new Map<string, HTMLDivElement>());

  // Track which notes are expanded — collapsed shows title +
  // preview; expanded shows the inline editor + attachments.
  // Newly-created notes auto-expand so the user lands in the
  // title field without an extra click.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const toggleExpanded = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const collapseAll = () => setExpandedIds(new Set());
  const anyExpanded = expandedIds.size > 0;

  // Initial load on drawer open. No SSE here — notes are
  // append-only-ish and the user is the only mutator; a one-shot
  // GET on open is fine.
  useEffect(() => {
    if (!open) return;
    if (status === "idle") void dispatch(fetchNotes());
  }, [dispatch, open, status]);

  // Close the drawer on Escape. A lightbox, if open, handles Escape
  // first in the capture phase and stops propagation, so this only
  // fires when no image is being viewed.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dispatch(setNotesOpen(false));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, dispatch]);

  // Consume a jump request: expand the note, scroll it into view,
  // clear the request. Waits for `notes` because the board can ask
  // for a note before this drawer has ever fetched its list — the
  // effect re-runs when the fetch lands and the row exists.
  useEffect(() => {
    if (!open || !focusNoteId) return;
    if (!notes.some((n) => n.id === focusNoteId)) return;
    setExpandedIds((prev) => new Set(prev).add(focusNoteId));
    // Next frame: the row only gets its expanded height after this
    // render commits, and scrolling before that lands short.
    const raf = requestAnimationFrame(() => {
      rowRefs.current.get(focusNoteId)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
    dispatch(clearNoteFocus());
    return () => cancelAnimationFrame(raf);
  }, [open, focusNoteId, notes, dispatch]);

  const onNew = async () => {
    const action = await dispatch(
      createNote({
        symbol_context: activeTabUi?.symbol ?? null,
        timeframe_context: activeTabUi?.timeframe ?? null,
      }),
    );
    if (createNote.fulfilled.match(action)) {
      // Auto-expand the new entry so the user can start typing
      // immediately.
      setExpandedIds((prev) => new Set(prev).add(action.payload.id));
    }
  };

  if (!open) return null;

  return (
    <aside className={styles.drawer} aria-label="Notes">
      <div className={styles.header}>
        <div className={styles.title}>
          <span className={styles.titleIco}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
              <path d="M5 4h11l4 4v12H5z" strokeLinejoin="round" />
              <path d="M9 12h6M9 16h4" strokeLinecap="round" />
            </svg>
          </span>
          Notes
        </div>
        <div className={styles.headerActions}>
          <button
            type="button"
            className={styles.newBtn}
            onClick={onNew}
            title="Create a new note tagged with the active chart's symbol + timeframe"
          >
            + New Note
          </button>
          <button
            type="button"
            className={styles.iconBtn}
            title="Browse every image from your notes and trades"
            aria-label="Open image board"
            onClick={() => dispatch(setImageBoardOpen(true))}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
              <rect x="3" y="4" width="18" height="16" rx="2" />
              <path d="M3 15l5-5 4 4 3-3 6 6" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="9" cy="9" r="1.4" />
            </svg>
          </button>
          <button
            type="button"
            className={styles.iconBtn}
            title="Collapse all open notes"
            aria-label="Collapse all notes"
            onClick={collapseAll}
            disabled={!anyExpanded}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
              <path d="M8 9l4-4 4 4M8 15l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button
            type="button"
            className={styles.iconBtn}
            title="Close"
            aria-label="Close notes drawer"
            onClick={() => dispatch(setNotesOpen(false))}
          >
            ×
          </button>
        </div>
      </div>

      <div className={styles.body}>
        {status === "loading" && notes.length === 0 ? (
          <div className={styles.loading}>Loading…</div>
        ) : notes.length === 0 ? (
          <div className={styles.empty}>
            No notes yet. Click + New Note to capture a market thought.
          </div>
        ) : (
          notes.map((note) => (
            <NoteRow
              key={note.id}
              note={note}
              expanded={expandedIds.has(note.id)}
              onToggle={() => toggleExpanded(note.id)}
              activeTabId={activeTabId}
              rowRef={(el) => {
                if (el) rowRefs.current.set(note.id, el);
                else rowRefs.current.delete(note.id);
              }}
            />
          ))
        )}
      </div>
    </aside>
  );
}

// -----------------------------------------------------------------
// Single note row — collapsed = title + preview; expanded =
// inline editor + attachment bar + delete.
// -----------------------------------------------------------------

function NoteRow({
  note,
  expanded,
  onToggle,
  activeTabId,
  rowRef,
}: {
  note: Note;
  expanded: boolean;
  onToggle: () => void;
  activeTabId: string;
  rowRef: (el: HTMLDivElement | null) => void;
}) {
  const dispatch = useAppDispatch();
  // Timeframe of the chart a screenshot would come from, in MT5
  // spelling — becomes the label's prefix ("H1-2"). Read here rather
  // than passed down so it is current at the moment of capture, not
  // at the moment the row rendered.
  const activeTf = useAppSelector(
    (s) => s.ui.byTab[activeTabId]?.timeframe,
  );
  const capturePrefix = activeTf ? tfMt5Label(activeTf) : undefined;
  const [title, setTitle] = useState(note.title);
  const [body, setBody] = useState(note.body);
  const [dirty, setDirty] = useState(false);
  // Index into the note's attachments, or null when closed. An
  // index rather than a url so the gallery can walk the set.
  const [lightbox, setLightbox] = useState<number | null>(null);
  // Attachment currently open in the annotator, or null.
  const [editing, setEditing] = useState<{
    attId: string;
    url: string;
    filename: string;
    label: string;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Lets the "+#" button drop a reference at the caret.
  const bodyRef = useRef<LinkedTextAreaHandle>(null);

  // Sync local draft back to props when the server-confirmed
  // version updates (e.g., the update thunk's fulfilled). Without
  // this, a successful save would still show "dirty" because the
  // local string isn't equal to the prop string by reference.
  useEffect(() => {
    setTitle(note.title);
    setBody(note.body);
    setDirty(false);
  }, [note.title, note.body]);

  const persist = () => {
    if (!dirty) return;
    void dispatch(updateNote({ id: note.id, patch: { title, body } }));
    // setDirty(false) handled by the useEffect above when the
    // fulfilled action replaces note.title/body.
  };

  const onTitleChange = (v: string) => {
    setTitle(v);
    setDirty(true);
  };
  const onBodyChange = (v: string) => {
    setBody(v);
    setDirty(true);
  };

  const onScreenshot = async () => {
    const result = await captureChart(activeTabId);
    if (!result) return;
    void dispatch(
      uploadNoteAttachment({
        id: note.id,
        blob: result.blob,
        filename: result.filename,
        prefix: capturePrefix,
      }),
    );
  };

  const onPickFile = () => fileInputRef.current?.click();

  const onFileChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Backend currently only accepts PNG (sniff on first 8 bytes).
    if (file.type !== "image/png") {
      window.alert("Only PNG files are supported.");
      return;
    }
    // No prefix: a file off disk didn't come from a chart, so
    // labelling it with whatever timeframe happens to be on screen
    // would be a lie. The server applies its generic prefix.
    void dispatch(
      uploadNoteAttachment({ id: note.id, blob: file, filename: file.name }),
    );
    // Reset the input so picking the same file twice in a row fires.
    e.target.value = "";
  };

  const onDeleteAttachment = (attId: string) => {
    void dispatch(deleteNoteAttachment({ id: note.id, attId }));
  };

  /** Commit an annotated image.
   *
   *  Saved as a NEW attachment, then the original is removed — deliberately
   *  in that order. Two reasons it isn't an in-place overwrite:
   *  the stream endpoint sets `Cache-Control: private, max-age=3600`, so a
   *  replaced blob would keep serving the stale picture for an hour; and
   *  uploading first means a failure here leaves the original intact rather
   *  than losing it. Side effect: the edited image moves to the end of the
   *  attachment row, since the slice appends. */
  const onSaveEditedAttachment = async (blob: Blob, filename: string) => {
    if (!editing) return;
    await dispatch(
      uploadNoteAttachment({
        id: note.id,
        blob,
        filename,
        // Carry the original number over. Without this the edited
        // copy would be minted as the next free number and a body
        // that says "see #2" would stop matching its picture.
        label: editing.label,
      }),
    ).unwrap();
    await dispatch(
      deleteNoteAttachment({ id: note.id, attId: editing.attId }),
    ).unwrap();
    setEditing(null);
  };

  const onDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm("Delete this note? Attachments will be deleted too."))
      return;
    void dispatch(deleteNote(note.id));
  };

  const onRename = (att: NoteAttachment, next: string) => {
    void dispatch(
      updateNoteAttachment({
        id: note.id,
        attId: att.id,
        patch: { label: next },
      }),
    );
  };

  /** Drop `[#H1-2]` into the body at the caret.
   *
   *  This is the reason labels exist: while writing you can see the
   *  thumbnail but you don't know what to call it, and typing the
   *  label by hand means looking away mid-sentence.
   *
   *  The button suppresses its own mousedown so the editor never
   *  blurs. A blur would fire `persist()`, and the PUT's response
   *  would then overwrite `body` through the sync effect —
   *  swallowing the token just inserted. */
  const insertRef = (label: string) => {
    bodyRef.current?.insertAtCaret(`[#${label}]`);
  };

  /** Open the image a `[#H1-2]` in the text points at. */
  const openRef = (label: string) => {
    const idx = note.attachments.findIndex(
      (a, i) => labelFor(a, i) === label,
    );
    if (idx >= 0) setLightbox(idx);
  };

  const gallery: GalleryImage[] = note.attachments.map((att, i) => ({
    id: att.id,
    url: attachmentUrl(note.id, att.id),
    label: labelFor(att, i),
    filename: att.filename,
    context: note.title || null,
  }));

  const contextLabel =
    note.symbol_context
      ? `${note.symbol_context}${note.timeframe_context ? ` · ${note.timeframe_context}` : ""}`
      : null;

  return (
    <div className={styles.note} ref={rowRef}>
      <div className={styles.noteHeader} onClick={onToggle}>
        {note.seq != null && (
          <span className={styles.seqBadge} title={`Note #${note.seq}`}>
            #{note.seq}
          </span>
        )}
        <span
          className={`${styles.noteTitle} ${!note.title ? styles.noteTitlePlaceholder : ""}`}
        >
          {note.title || "(untitled)"}
        </span>
        {contextLabel && (
          <span className={styles.contextBadge}>{contextLabel}</span>
        )}
        <span className={styles.timestamp}>
          {formatTimestamp(note.created_at)}
        </span>
        <button
          type="button"
          className={styles.deleteBtn}
          onClick={onDelete}
          title="Delete this note"
          aria-label="Delete note"
        >
          ×
        </button>
      </div>

      {expanded ? (
        <>
          <input
            type="text"
            className={styles.titleInput}
            placeholder="Title (one-line summary)"
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
            onBlur={persist}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                persist();
              }
            }}
          />
          <LinkedTextArea
            handleRef={bodyRef}
            className={styles.bodyInput}
            placeholder="Write your thoughts…"
            value={body}
            onChange={onBodyChange}
            onCommit={persist}
            onRefClick={openRef}
          />
          <div className={styles.actionRow}>
            <span
              className={`${styles.saveStatus} ${dirty ? styles.saveStatusDirty : styles.saveStatusSaved}`}
            >
              {dirty ? "● Unsaved" : "✓ Saved"}
            </span>
            <button
              type="button"
              className={styles.saveBtn}
              onClick={persist}
              disabled={!dirty}
              title="Save this note (or press Enter in the title / Ctrl+Enter in the body)"
            >
              Save
            </button>
          </div>
          <div className={styles.attachmentBar}>
            {note.attachments.map((att, i) => {
              const label = labelFor(att, i);
              return (
                <AttachmentThumb
                  key={att.id}
                  url={attachmentUrl(note.id, att.id)}
                  filename={att.filename}
                  bytes={att.bytes}
                  label={label}
                  citedIn="the note body"
                  onOpen={() => setLightbox(i)}
                  onAnnotate={() =>
                    setEditing({
                      attId: att.id,
                      url: attachmentUrl(note.id, att.id),
                      filename: att.filename,
                      label,
                    })
                  }
                  onDelete={() => onDeleteAttachment(att.id)}
                  onRename={(v) => onRename(att, v)}
                  onInsertRef={() => insertRef(label)}
                />
              );
            })}
            <div className={styles.attachmentActions}>
              <button
                type="button"
                className={styles.attachmentAction}
                onClick={onScreenshot}
                title="Capture the active chart and attach it"
              >
                Screenshot chart
              </button>
              <button
                type="button"
                className={styles.attachmentAction}
                onClick={onPickFile}
                title="Upload a PNG from disk"
              >
                Upload PNG
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png"
                style={{ display: "none" }}
                onChange={onFileChosen}
              />
            </div>
          </div>
        </>
      ) : (
        note.body && (
          <div className={styles.bodyPreview} onClick={onToggle}>
            <RefText text={note.body} onRefClick={openRef} />
          </div>
        )
      )}

      {editing && (
        <ImageEditor
          src={editing.url}
          filename={editing.filename}
          onCancel={() => setEditing(null)}
          onSave={onSaveEditedAttachment}
        />
      )}

      {lightbox !== null && (
        <ImageLightbox
          items={gallery}
          startIndex={lightbox}
          onClose={() => setLightbox(null)}
          onEdit={(item) => {
            setLightbox(null);
            setEditing({
              attId: item.id,
              url: item.url,
              filename: item.filename,
              label: item.label,
            });
          }}
          onDelete={(item) => onDeleteAttachment(item.id)}
        />
      )}
    </div>
  );
}

// Shorten "2026-05-25T10:23:14.123Z" → "May 25, 10:23" in the chart display
// zone (UTC) so note times match everything else. See chartTime.ts.
const formatTimestamp = fmtShortDateTimeIso;
